import React from 'react'
import * as zarr from 'zarrita'
import type { Dataset, ControlsProps } from './types'
import { BandSelector } from '../components/SharedControls'

const S2_L1C_ZARR = 'S2A_MSIL1C_20260721T102041_N0512_R065_T32UPU_20260721T153621.zarr'
const PROXY_BASE = `/eopf-examples/${S2_L1C_ZARR}`

// 3 multiscale levels: r2 (120m), r6 (360m), r12 (720m)
const LEVELS = ['r2', 'r6', 'r12']

// Custom store that injects the multiscales layout into root zarr.json
class EopfS2L1cStore {
  async get(key: string): Promise<Uint8Array | undefined> {
    const k = key.startsWith('/') ? key.slice(1) : key
    const resp = await fetch(`${PROXY_BASE}/${k}`)
    if (!resp.ok) return undefined
    if (k === 'zarr.json') {
      const meta = await resp.json()
      meta.attributes = {
        ...meta.attributes,
        multiscales: {
          layout: LEVELS.map((l) => ({ asset: `multiscales/${l}` })),
        },
      }
      return new TextEncoder().encode(JSON.stringify(meta))
    }
    return new Uint8Array(await resp.arrayBuffer())
  }

  async getRange(
    key: string,
    range: { offset?: number; length?: number; suffixLength?: number },
    options?: { signal?: AbortSignal }
  ): Promise<Uint8Array | undefined> {
    const k = key.startsWith('/') ? key.slice(1) : key
    const rangeHeader =
      range.suffixLength !== undefined
        ? `bytes=-${range.suffixLength}`
        : `bytes=${range.offset}-${(range.offset ?? 0) + (range.length ?? 0) - 1}`
    const resp = await fetch(`${PROXY_BASE}/${k}`, {
      headers: { Range: rangeHeader },
      signal: options?.signal,
    })
    if (!resp.ok) return undefined
    return new Uint8Array(await resp.arrayBuffer())
  }
}

const BANDS = ['b01', 'b02', 'b03', 'b04', 'b05', 'b06', 'b07', 'b08', 'b8a', 'b09', 'b10', 'b11', 'b12']
type Band = typeof BANDS[number]

type S2L1cState = { band: Band }

const Controls = ({ state, setState }: ControlsProps<S2L1cState>) => (
  <BandSelector
    value={state.band}
    options={BANDS}
    onChange={(band) => setState({ band })}
    label="Band"
  />
)

const eopfS2L1c: Dataset<S2L1cState> = {
  id: 'eopf-s2-l1c',
  tags: ['sentinel-2', 'optical', 'austria'],
  source: PROXY_BASE,
  variable: 'b04',
  clim: [0, 1],
  colormap: 'greys',
  zarrVersion: 3,
  // Native CRS: EPSG:32632 (UTM zone 32N)
  proj4: '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs',
  spatialDimensions: { lat: 'y', lon: 'x' },
  bounds: [600000, 5290200, 709800, 5400000],
  bounds4326: [10.3343, 47.7310, 11.8524, 48.7450],
  latIsAscending: false,
  fillValue: 0,
  center: [11.09, 48.23],
  zoom: 9,
  info: 'Sentinel-2 L1C MSI',
  subtitle: 'Austria · UTM 32N · 120 m coarsest · 2026-07-21',
  sourceInfo:
    'Sentinel-2A MSI L1C product (EOPF zarr v3) — TOA reflectance, tile T32UPU, acquired 2026-07-21.',
  store: Promise.resolve(new EopfS2L1cStore() as zarr.Readable),
  defaultState: { band: 'b04' },
  Controls,
  buildLayerProps: (state) => ({
    variable: state.band,
    selector: {},
  }),
}

export default eopfS2L1c
